import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faCommentDots, faVideo, faGamepad, faUsers, faFilter, faUpload, faClipboardList, faChartLine, faClipboardCheck, faShieldAlt, faClock, faRocket } from '@fortawesome/free-solid-svg-icons';
import Header from '../../component/layout/header';
import Footer from '../../component/layout/footer';
import Navigate from '../../../../util/Navigate';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import IndieSubmenu from '../../component/section/publishers/IndieSubmenu';

function PublishersPlayersPage() {

    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

    return (
        <>
            <Helmet prioritizeSeoTags>
                <meta charSet="utf-8" />
                <title>Find Play Testers for Your Game | Glitch Gaming</title>
                <meta name="description" content="Discover dedicated play testers to test your game. Utilize Glitch's platform to search, sort, and engage with players who provide valuable feedback through text, video, and audio." />
                <meta name="robots" content="index, follow" />
                <meta property="og:title" content="Find Play Testers for Your Game | Glitch" />
                <meta property="og:description" content="Discover dedicated play testers to test your game. Utilize Glitch's platform to search, sort, and engage with players who provide valuable feedback through text, video, and audio." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={currentUrl} />
                <meta property="og:image" content="https://www.glitch.fun/assets/images/publishers/play_testers_banner.webp" />
            </Helmet>
            <Header />

            {/* Hero Section */}
            <section className="database-hero">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6 text-center text-lg-start">
                            <h1 className="display-4 fw-bold mb-4 pt-5">
                                Perfect Your Game with <span className="text-gradient">Real Player Feedback</span>
                            </h1>
                            <p className="lead mb-5">Access dedicated play testers across all platforms. Filter by game genre, platform expertise, and testing experience.</p>
                            <div className="cta-group">
                                <Link to={Navigate.publishersOnboardingStep1Page()} className="btn btn-primary btn-lg me-3 mb-3">
                                    Start Free Search <FontAwesomeIcon icon={faRocket} className="ms-2" />
                                </Link>
                                <Link to={Navigate.demoPage()} className="btn btn-outline-light btn-lg mb-3">
                                    Watch Demo Video
                                </Link>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="hero-visual mt-5 mt-lg-0">
                                <img
                                    src="/assets/images/publishers/play_testers_banner.webp"
                                    alt="Play Testing Preview"
                                    className="img-fluid rounded-3 shadow-lg border border-3 border-white"
                                />
                                <div className="highlight-card pc">
                                    <FontAwesomeIcon icon={faGamepad} className="me-2" />
                                    25,000+ PC Gamers
                                </div>
                                <div className="highlight-card console">
                                    <FontAwesomeIcon icon={faChartLine} className="me-2" />
                                    15,000+ Console Players
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Play Testing Matters Section */}
            <section className="why-playtesting py-5 bg-light">
                <div className="container text-black">
                    <div className="text-center mb-5">
                        <h2 className="display-5 fw-bold mb-3 text-black">Why Play Testing Matters</h2>
                        <p className="lead">Ensure your game delivers an exceptional experience by identifying and fixing issues before launch</p>
                    </div>
                    <div className="row g-4">
                        <div className="col-md-4">
                            <div className="benefit-card h-100 p-4 bg-white rounded-3 shadow-sm">
                                <div className="icon-wrapper bg-primary mb-3">
                                    <FontAwesomeIcon icon={faClipboardCheck} className="text-white" />
                                </div>
                                <h3 className='text-black'>Reduce Negative Reviews</h3>
                                <p>Games with extensive play testing see a 30% decrease in negative reviews related to bugs.</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="benefit-card h-100 p-4 bg-white rounded-3 shadow-sm">
                                <div className="icon-wrapper bg-success mb-3">
                                    <FontAwesomeIcon icon={faUsers} className="text-white" />
                                </div>
                                <h3 className='text-black'>Improve Retention</h3>
                                <p>Well-tested games have a 25% higher player retention rate after the first week.</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="benefit-card h-100 p-4 bg-white rounded-3 shadow-sm">
                                <div className="icon-wrapper bg-danger mb-3">
                                    <FontAwesomeIcon icon={faChartLine} className="text-white" />
                                </div>
                                <h3 className='text-black'>Enhance Player Satisfaction</h3>
                                <p>Games that incorporate player feedback during development see increased player satisfaction scores.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Value Propositions */}
            <section className="value-propositions py-5 text-black" style={{ background: '#f0f4ff' }}>
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="display-5 fw-bold mb-3 text-black">Features</h2>
                    </div>
                    <div className="row g-4">
                        <div className="col-md-4">
                            <div className="value-card h-100 bg-white">
                                <div className="icon-wrapper bg-primary">
                                    <FontAwesomeIcon icon={faSearch} className="fa-2x" />
                                </div>
                                <h3 className='text-black'>Search Through Our Play Tester Database</h3>
                                <p>Access a vast database of play testers eager to test your game. Filter and sort by their gaming preferences, expertise, and more.</p>
                                <ul className="value-list">
                                    <li>Filter by Game Genres and Preferences</li>
                                    <li>Review In-Depth Bios</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="value-card h-100 bg-white">
                                <div className="icon-wrapper bg-success">
                                    <FontAwesomeIcon icon={faCommentDots} className="fa-2x" />
                                </div>
                                <h3 className='text-black'>Collect Valuable Feedback</h3>
                                <p>Receive detailed feedback from play testers in text, video, and audio formats. Understand their experiences to improve your game.</p>
                                <ul className="value-list">
                                    <li>Text Feedback</li>
                                    <li>Video Feedback</li>
                                    <li>Audio Feedback</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="value-card h-100 bg-white">
                                <div className="icon-wrapper bg-danger">
                                    <FontAwesomeIcon icon={faVideo} className="fa-2x" />
                                </div>
                                <h3 className='text-black'>Review Gameplay Footage</h3>
                                <p>Play testers can upload their gameplay sessions, allowing you to observe their interactions and identify areas for improvement.</p>
                                <ul className="value-list">
                                    <li>Upload Gameplay Videos</li>
                                    <li>Observe Player Interactions</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {/* Workflow Steps */}
            {/* Workflow Steps */}
            <section className="workflow-steps py-5 text-black">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="display-5 fw-bold mb-3 text-black">Streamlined Play Testing Process</h2>
                        <p className="lead">Three simple steps to perfect your game</p>
                    </div>

                    <div className="row g-5 position-relative">
                        {/* Step 1 */}
                        <div className="col-lg-4">
                            <div className="step-card bg-white p-4 p-lg-5 rounded-4 shadow-lg h-100">
                                <div className="step-header d-flex align-items-center mb-4">
                                    <div className="step-number bg-primary text-white rounded-circle">1</div>
                                    <h3 className="h4 mb-0 ms-3 text-black">Find Testers</h3>
                                </div>
                                <p className="text-muted mb-4 text-center">Filter our database of testers</p>
                                <div className="step-visual overflow-hidden rounded-3" style={{ height: '200px' }}>
                                    <img
                                        src="/assets/images/publishers/player_tester_bio.png"
                                        alt="Tester Search"
                                        className="img-fluid rounded-2 w-100 h-100"
                                        style={{ objectFit: 'cover' }}
                                    />
                                </div>
                                <div className="step-features mt-4">
                                    <div className="d-flex align-items-center mb-2">
                                        <FontAwesomeIcon icon={faFilter} className="me-2 text-primary" />
                                        <span>Advanced Filters</span>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
                                        <span>Tester Ratings</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Step 2 */}
                        <div className="col-lg-4">
                            <div className="step-card bg-white p-4 p-lg-5 rounded-4 shadow-lg h-100">
                                <div className="step-header d-flex align-items-center mb-4">
                                    <div className="step-number bg-success text-white rounded-circle">2</div>
                                    <h3 className="h4 mb-0 ms-3 text-black">Gather Feedback</h3>
                                </div>
                                <p className="text-muted mb-4 text-center">Collect structured player insights</p>
                                <div className="step-visual overflow-hidden rounded-3" style={{ height: '200px' }}>
                                    <video
                                        src="/assets/videos/publishers/gamer_bugs.mp4"
                                        className="img-fluid rounded-2 w-100 h-100"
                                        autoPlay loop muted playsInline
                                        style={{ objectFit: 'cover' }}
                                    ></video>
                                </div>
                                <div className="step-features mt-4">
                                    <div className="d-flex align-items-center mb-2">
                                        <FontAwesomeIcon icon={faCommentDots} className="me-2 text-success" />
                                        <span>Multi-Format Feedback</span>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon icon={faVideo} className="me-2 text-success" />
                                        <span>Gameplay Recordings</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Step 3 */}
                        <div className="col-lg-4">
                            <div className="step-card bg-white p-4 p-lg-5 rounded-4 shadow-lg h-100">
                                <div className="step-header d-flex align-items-center mb-4">
                                    <div className="step-number bg-danger text-white rounded-circle">3</div>
                                    <h3 className="h4 mb-0 ms-3 text-black">Analyze & Improve</h3>
                                </div>
                                <p className="text-muted mb-4 text-center">Actionable insights for refinement</p>
                                <div className="step-visual overflow-hidden rounded-3" style={{ height: '200px' }}>
                                    <img
                                        src="/assets/images/publishers/play_tester_questions.png"
                                        alt="Analytics Dashboard"
                                        className="img-fluid rounded-2 w-100 h-100"
                                        style={{ objectFit: 'cover' }}
                                    />
                                </div>
                                <div className="step-features mt-4">
                                    <div className="d-flex align-items-center mb-2">
                                        <FontAwesomeIcon icon={faChartLine} className="me-2 text-danger" />
                                        <span>Analytics Dashboard</span>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon icon={faClipboardCheck} className="me-2 text-danger" />
                                        <span>Priority Fix Tracking</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="database-cta py-5">
                <div className="container text-center">
                    <h2 className="mb-4">Start Perfecting Your Game Today</h2>
                    <p className="cta-subtitle mb-5">Join 1,000+ developers who shipped better games through Glitch</p>
                    <div className="cta-wrapper">
                        <Link to={Navigate.publishersOnboardingStep1Page()} className="btn btn-primary btn-lg px-5 py-3 me-3">
                            Begin Free Testing
                        </Link>
                        <Link to={Navigate.demoPage()} className="btn btn-outline-light btn-lg px-5 py-3">
                            Request Personalized Demo
                        </Link>
                    </div>
                    <div className="trust-badges mt-4">
                        <div className="badge-item d-flex align-items-center justify-content-center gap-2">
                            <FontAwesomeIcon icon={faShieldAlt} />
                            <span>Secure Testing Environment</span>
                        </div>
                        <div className="badge-item d-flex align-items-center justify-content-center gap-2 mt-2">
                            <FontAwesomeIcon icon={faClock} />
                            <span>24/7 Tester Availability</span>
                        </div>
                    </div>
                </div>
            </section>

            <IndieSubmenu />
            <Footer />

            <style jsx>{`
                .database-hero {
                    padding: 100px 0;
                    background: linear-gradient(135deg, #1a1b2f 0%, #080e37 100%);
                    color: white;
                    position: relative;
                    overflow: hidden;
                }

                .text-gradient {
                    background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .hero-visual {
                    position: relative;
                    border-radius: 20px;
                    overflow: hidden;
                    transform: perspective(1000px) rotateY(-5deg);
                    box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
                }

                .highlight-card {
                    position: absolute;
                    padding: 1rem 1.5rem;
                    border-radius: 12px;
                    backdrop-filter: blur(10px);
                    background: rgba(255,255,255,0.1);
                    border: 1px solid rgba(255,255,255,0.2);
                    max-width: 250px;
                    animation: float 6s ease-in-out infinite;
                }

                .highlight-card.pc { top: 20%; left: -5%; }
                .highlight-card.console { bottom: 20%; right: -5%; }

                @keyframes float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-20px); }
                }

                .why-playtesting {
                    padding: 80px 0;
                }

                .benefit-card {
                    transition: transform 0.3s ease;
                }

                .benefit-card:hover {
                    transform: translateY(-5px);
                }

                .icon-wrapper {
                    width: 50px;
                    height: 50px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .value-propositions {
                    background: #f8f9ff;
                }

                .value-card {
                    background: white;
                    padding: 2rem;
                    border-radius: 16px;
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                    transition: transform 0.3s ease;
                }

                .value-card:hover {
                    transform: translateY(-10px);
                }

                .value-propositions {
    padding: 80px 0;
}

.value-card {
    background: white;
    padding: 2rem;
    border-radius: 16px;
    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
    transition: transform 0.3s ease;
    border: 1px solid rgba(0,0,0,0.05);
}

.value-card:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
}

                .workflow-steps {
                    background: linear-gradient(180deg, #ffffff 0%, #f8f9ff 100%);
                }

                .step-number {
                    width: 50px;
                    height: 50px;
                    font-size: 1.5rem;
                    font-weight: 700;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .step-card {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                    background: white;
                    border: 1px solid rgba(0,0,0,0.05);
                }

                .step-card:hover {
                    transform: translateY(-10px);
                    box-shadow: 0 25px 50px -12px rgba(0,0,0,0.15);
                }

                .progress-line {
                    height: 4px;
                    width: 60%;
                    left: 20%;
                }

                .progress-line .line {
                    background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
                    height: 100%;
                    width: 100%;
                }

                .database-cta {
                    padding: 100px 0;
                    background: linear-gradient(135deg, #ff0052 0%, #9400ff 100%);
                    color: white;
                }

                @media (max-width: 768px) {
                    .database-hero {
                        padding: 60px 0;
                    }
                    
                    .hero-visual {
                        transform: none;
                    }
                    
                    .highlight-card {
                        position: relative;
                        margin: 1rem auto;
                        animation: none;
                    }
                }
            `}</style>
        </>
    );
}

export default PublishersPlayersPage;